import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, IconButton, Tooltip,
  FormControl, Select, MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { typography } from '../../../../styles/theme';
import {
  getUfBuildingTypeIcon,
  UF_BUILDING_TYPE_ICON_LIST_SX,
  UF_BUILDING_TYPE_ICON_TILE_LIST_SX,
} from '../../../utils/ufBuildingTypeIcons';
import {
  NO_OPERATION_VALUE,
  resolveUnitOperation,
  formatUserLotCommercial,
} from '../../../utils/userLotFormUtils';
import { isHighlightedUnit, UNIT_HIGHLIGHT_SX, useScrollIntoViewWhen } from './unitHighlight';
import { OPERATIONS, OPERATION_LABEL } from '../../../utils/listingCoreConstants';
import { ACTION_MENU_LIST_PROPS, ACTION_MENU_PAPER_PROPS } from '../../ActionMenuItem';
import {
  listFiltersBoxSx,
  listSearchToolbarSx,
} from '../../UserLotListsPanel/UserLotListsPanel.styles';
import { withAlpha } from '../../../utils/sharedBranding';
import { PANEL_SECTION_TITLE_SX, SECTION_BAR_ICON_BUTTON_SX } from './formBlockUi';
import CommercialStatusDot from '../../../../Leads/components/CommercialStatusDot';

const NO_LISTS_VALUE = '__NO_LISTS__';
const BG_ELEVATED = '#242424';
const TEXT_PRIMARY = '#D4D4D4';

const unitFiltersToolbarZoneSx = {
  flexShrink: 0,
  borderBottom: '1px solid rgba(255,255,255,0.06)',
  // bgcolor: 'rgba(0,0,0,0.08)',
};

const filterToolbarSelectSx = {
  height: 44,
  minHeight: 44,
  backgroundColor: BG_ELEVATED,
  borderRadius: '8px',
  color: '#ffffff',
  fontSize: '13px',
  fontFamily: typography.fontFamily.primary,
  transition: 'border-color 0.15s',
  '& .MuiSelect-select': {
    py: 0,
    px: 1.5,
    height: 44,
    minHeight: 44,
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.12)',
    borderWidth: '1px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.20)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.28)',
    borderWidth: '1px',
  },
  '& .MuiSelect-icon': {
    color: 'rgba(255,255,255,0.65)',
    fontSize: 18,
    right: 10,
  },
};

const filterMenuProps = {
  PaperProps: {
    sx: {
      ...ACTION_MENU_PAPER_PROPS.sx,
      maxHeight: 280,
    },
  },
  MenuListProps: ACTION_MENU_LIST_PROPS,
};

const renderOperationFilterValue = (value) => {
  if (!value) return 'Operación';
  if (value === NO_OPERATION_VALUE) return 'Sin operación';
  return OPERATION_LABEL[value] ?? value;
};

const renderListFilterValue = (value, listFilterOptions) => {
  if (!value) return 'Lista';
  if (value === NO_LISTS_VALUE) return 'Sin lista';
  const match = listFilterOptions.find((list) => list.id === value);
  return match?.name || 'Lista';
};

export const getUnitListIds = (unit) => {
  const idsFromLists = Array.isArray(unit?.lists) ? unit.lists.map((x) => x?.id).filter(Boolean) : [];
  const idsFromListIds = Array.isArray(unit?.list_ids) ? unit.list_ids.filter(Boolean) : [];
  const legacyId = unit?.list_id ? [unit.list_id] : [];
  return Array.from(new Set([...idsFromLists, ...idsFromListIds, ...legacyId]));
};

export const filterUnitsByListId = (units, listId) => {
  if (!listId) return units;
  const targetId = String(listId);
  const rows = Array.isArray(units) ? units : [];
  return rows.filter((unit) =>
    getUnitListIds(unit).some((id) => String(id) === targetId)
  );
};

const unitIsUnlisted = (unit) => {
  const ids = getUnitListIds(unit);
  const hasLegacyListNameOnly = !ids.length && !!unit?.list_name;
  return !ids.length && !hasLegacyListNameOnly;
};

const formatUnitCommercialLine = (unit) => formatUserLotCommercial(unit);

const UnitCard = ({ unit, onSelect, publicView = false, highlighted = false }) => {
  const cardRef = useScrollIntoViewWhen(highlighted);
  const title = publicView
    ? (unit.title || 'Sin título')
    : (unit.title || unit.unit_label || 'Sin etiqueta');
  const commercialLine = formatUnitCommercialLine(unit);
  const BuildingTypeIcon = getUfBuildingTypeIcon(unit.building_type);

  return (
    <Box
      ref={cardRef}
      data-testid={highlighted ? 'unit-highlighted' : undefined}
      onClick={() => onSelect(unit.id)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.5,
        cursor: 'pointer',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        '&:last-of-type': { borderBottom: 'none' },
        transition: 'background-color 0.15s',
        '&:hover': {
          bgcolor: 'rgba(255,255,255,0.04)',
          '& .unit-list-chevron': { color: 'rgba(255,255,255,0.55)' },
        },
        '&:active': { bgcolor: 'rgba(255,255,255,0.07)' },
        ...(highlighted ? UNIT_HIGHLIGHT_SX : {}),
      }}
    >
      <Box sx={UF_BUILDING_TYPE_ICON_TILE_LIST_SX}>
        <BuildingTypeIcon sx={UF_BUILDING_TYPE_ICON_LIST_SX} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: typography.fontWeight.semibold,
            color: '#d4d4d4',
            fontFamily: typography.fontFamily.primary,
            lineHeight: '19px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </Typography>
        {/* Resumen comercial: solo dueño/editor (nunca en el share). */}
        {!publicView && <CommercialStatusDot summary={unit.commercial_summary} />}
        </Box>

        {commercialLine ? (
          <Typography
            sx={{
              mt: 0.25,
              fontSize: '12px',
              fontWeight: typography.fontWeight.regular,
              color: '#ababab',
              fontFamily: typography.fontFamily.primary,
              lineHeight: '16px',
              textTransform: 'uppercase',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {commercialLine}
          </Typography>
        ) : (
          <Typography
            sx={{
              mt: 0.25,
              fontSize: '12px',
              color: 'rgba(255,255,255,0.28)',
              fontFamily: typography.fontFamily.primary,
              lineHeight: '16px',
            }}
          >
            Sin datos comerciales
          </Typography>
        )}
      </Box>

      <ChevronRightIcon
        className="unit-list-chevron"
        sx={{
          fontSize: 20,
          color: 'rgba(255,255,255,0.25)',
          flexShrink: 0,
          transition: 'color 0.15s',
        }}
      />
    </Box>
  );
};

const UnitListView = ({
  units = [],
  userLotLists = [],
  onSelectUnit,
  onAddUnit,
  canEdit,
  hideListFilter = false,
  showAddUnit = true,
  publicView = false,
  accentColor = null,
  embedded = false,
  emptyHint = null,
  /** UF recién creada: resaltado temporal + scroll. */
  highlightUnitId = null,
}) => {
  const [listFilter, setListFilter] = useState('');
  const [operationFilter, setOperationFilter] = useState('');

  const unitIdsKey = useMemo(() => units.map((u) => u.id).join(','), [units]);

  useEffect(() => {
    setListFilter('');
    setOperationFilter('');
  }, [unitIdsKey]);

  const availableOperations = useMemo(() => {
    const found = new Set(units.map((u) => resolveUnitOperation(u)).filter(Boolean));
    return OPERATIONS.filter((o) => found.has(o.value)).map((o) => o.value);
  }, [units]);

  const hasUnitsWithoutOperation = useMemo(
    () => units.some((u) => !resolveUnitOperation(u)),
    [units],
  );

  const hasUnlistedUnits = useMemo(() => units.some(unitIsUnlisted), [units]);

  const listFilterOptions = useMemo(() => {
    const usedIds = new Set();
    units.forEach((u) => getUnitListIds(u).forEach((id) => usedIds.add(id)));
    const fromCatalog = (Array.isArray(userLotLists) ? userLotLists : [])
      .filter((l) => l?.id && usedIds.has(l.id));
    const fromUnits = [];
    units.forEach((u) => {
      if (!Array.isArray(u.lists)) return;
      u.lists.forEach((li) => {
        if (li?.id && usedIds.has(li.id) && !fromCatalog.some((c) => c.id === li.id)) {
          fromUnits.push({ id: li.id, name: li.name || 'Lista' });
        }
      });
    });
    return [...fromCatalog, ...fromUnits];
  }, [units, userLotLists]);

  const showListFilters = !hideListFilter && (listFilterOptions.length > 0 || hasUnlistedUnits);
  const showOperationFilters = availableOperations.length > 0 || hasUnitsWithoutOperation;
  const hasActiveFilters = !!listFilter || !!operationFilter;
  const showFilters = units.length > 1 && (showListFilters || showOperationFilters);

  const filteredUnits = useMemo(() => {
    return units.filter((unit) => {
      if (operationFilter === NO_OPERATION_VALUE) {
        if (resolveUnitOperation(unit)) return false;
      } else if (operationFilter && resolveUnitOperation(unit) !== operationFilter) {
        return false;
      }
      if (!listFilter) return true;
      if (listFilter === NO_LISTS_VALUE) return unitIsUnlisted(unit);
      return getUnitListIds(unit).includes(listFilter);
    });
  }, [units, listFilter, operationFilter]);

  const clearFilters = () => {
    setListFilter('');
    setOperationFilter('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {!embedded && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          // bgcolor: 'rgba(0,0,0,0.15)',
        }}
      >
        <Typography sx={PANEL_SECTION_TITLE_SX}>
          {hasActiveFilters
            ? `${filteredUnits.length} de ${units.length} unidades`
            : `${units.length} ${units.length === 1 ? 'unidad' : 'unidades'}`}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {hasActiveFilters && (
            <Tooltip title="Limpiar filtros" placement="top" arrow>
              <IconButton
                size="small"
                onClick={clearFilters}
                sx={{
                  width: 26,
                  height: 26,
                  color: accentColor ? withAlpha(accentColor, 0.82) : 'rgba(255,255,255,0.45)',
                  '&:hover': {
                    color: accentColor || '#fff',
                    bgcolor: accentColor ? withAlpha(accentColor, 0.12) : 'rgba(255,255,255,0.07)',
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
          {canEdit && showAddUnit && onAddUnit && (
            <Tooltip title="Agregar unidad" placement="top" arrow>
              <IconButton
                size="small"
                onClick={onAddUnit}
                aria-label="Agregar unidad"
                sx={SECTION_BAR_ICON_BUTTON_SX}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
      )}

      {showFilters && (
        <Box sx={unitFiltersToolbarZoneSx}>
          <Box sx={{ ...listFiltersBoxSx, px: 2, pt: 1.25, pb: 1.25 }}>
            <Box sx={listSearchToolbarSx}>
              {showOperationFilters && (
                <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                  <Select
                    value={operationFilter}
                    onChange={(e) => setOperationFilter(e.target.value)}
                    displayEmpty
                    renderValue={(value) => (
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: typography.fontFamily.primary,
                          fontSize: '13px',
                          color: value ? '#ffffff' : 'rgba(255,255,255,0.65)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {renderOperationFilterValue(value)}
                      </Typography>
                    )}
                    IconComponent={ExpandMoreIcon}
                    sx={filterToolbarSelectSx}
                    MenuProps={filterMenuProps}
                  >
                    <MenuItem value="">
                      <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px', fontFamily: typography.fontFamily.primary }}>
                        Todas las operaciones
                      </Typography>
                    </MenuItem>
                    {availableOperations.map((value) => (
                      <MenuItem key={value} value={value}>
                        <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px', fontFamily: typography.fontFamily.primary }}>
                          {OPERATION_LABEL[value] ?? value}
                        </Typography>
                      </MenuItem>
                    ))}
                    {hasUnitsWithoutOperation && (
                      <MenuItem value={NO_OPERATION_VALUE}>
                        <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px', fontFamily: typography.fontFamily.primary }}>
                          Sin operación
                        </Typography>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              )}

              {showListFilters && (
                <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                  <Select
                    value={listFilter}
                    onChange={(e) => setListFilter(e.target.value)}
                    displayEmpty
                    renderValue={(value) => (
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: typography.fontFamily.primary,
                          fontSize: '13px',
                          color: value ? '#ffffff' : 'rgba(255,255,255,0.65)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {renderListFilterValue(value, listFilterOptions)}
                      </Typography>
                    )}
                    IconComponent={ExpandMoreIcon}
                    sx={filterToolbarSelectSx}
                    MenuProps={filterMenuProps}
                  >
                    <MenuItem value="">
                      <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px', fontFamily: typography.fontFamily.primary }}>
                        Todas las listas
                      </Typography>
                    </MenuItem>
                    {listFilterOptions.map((list) => (
                      <MenuItem key={list.id} value={list.id}>
                        <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px', fontFamily: typography.fontFamily.primary }}>
                          {list.name}
                        </Typography>
                      </MenuItem>
                    ))}
                    {hasUnlistedUnits && (
                      <MenuItem value={NO_LISTS_VALUE}>
                        <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px', fontFamily: typography.fontFamily.primary }}>
                          Sin lista
                        </Typography>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              )}

            </Box>
          </Box>
        </Box>
      )}

      <Box sx={{ flex: 1 }}>
        {filteredUnits.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', fontFamily: typography.fontFamily.primary }}>
              {units.length === 0 && !hasActiveFilters
                ? (emptyHint || 'No hay unidades.')
                : 'Ninguna unidad coincide con los filtros.'}
            </Typography>
          </Box>
        ) : (
          filteredUnits.map((unit) => (
            <UnitCard
              key={unit.id}
              unit={unit}
              onSelect={onSelectUnit}
              publicView={publicView}
              highlighted={isHighlightedUnit(highlightUnitId, unit.id)}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

export default UnitListView;
